import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Listing, ListingType } from '../../types/index';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import ListingSubmitted from './ListingSubmitted';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

interface FormData {
  title: string;
  description: string;
  type: ListingType | '';
  dateRange: {
    start: string;
    end: string;
  };
  timeRange: {
    start: string;
    end: string;
  };
  location: {
    address: string;
    city: string;
    state: string;
    zip: string;
    coordinates?: {
      lat: number;
      lng: number;
    };
  };
  images: string[];
  // Garage Sale specific fields
  items?: string[];
  // Estate Sale specific fields
  estateCompany?: string;
  previewDate?: string;
  previewTime?: string;
  // Curb Alert specific fields
  itemCondition?: string;
  availableUntil?: string;
}

type Step = 'type' | 'details' | 'location' | 'preview' | 'review';

export default function ListingForm() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<Step>('type');
  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const marker = useRef<mapboxgl.Marker | null>(null);
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    type: '',
    dateRange: {
      start: '',
      end: '',
    },
    timeRange: {
      start: '',
      end: '',
    },
    location: {
      address: '',
      city: '',
      state: '',
      zip: '',
    },
    images: [],
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [submitted, setSubmitted] = useState(false);

  // Initialize map when location step is active
  useEffect(() => {
    if (currentStep === 'location' && mapContainer.current && !map.current) {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/light-v11',
        center: [-85.3088, 35.0456], // Chattanooga coordinates
        zoom: 12
      });

      map.current.addControl(new mapboxgl.NavigationControl());

      // Add click handler for dropping pin
      map.current.on('click', (e) => {
        const { lng, lat } = e.lngLat;
        updateMarker(lng, lat);
        reverseGeocode(lng, lat);
      });

      return () => {
        map.current?.remove();
        map.current = null;
      };
    }
  }, [currentStep]);

  const updateMarker = (lng: number, lat: number) => {
    if (!map.current) return;

    // Remove existing marker
    if (marker.current) {
      marker.current.remove();
    }

    // Add new marker
    marker.current = new mapboxgl.Marker({
      color: '#f97316',
      draggable: true
    })
      .setLngLat([lng, lat])
      .addTo(map.current);

    // Update coordinates on drag end
    marker.current.on('dragend', () => {
      const { lng, lat } = marker.current!.getLngLat();
      reverseGeocode(lng, lat);
    });

    // Update form data
    setFormData(prev => ({
      ...prev,
      location: {
        ...prev.location,
        coordinates: { lat, lng }
      }
    }));
  };

  const reverseGeocode = async (lng: number, lat: number) => {
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${mapboxgl.accessToken}`
      );
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const feature = data.features[0];
        const context = feature.context || [];
        const address = feature.text;
        const city = context.find((c: any) => c.id.startsWith('place'))?.text || '';
        const state = context.find((c: any) => c.id.startsWith('region'))?.text || '';
        const zip = context.find((c: any) => c.id.startsWith('postcode'))?.text || '';

        setFormData(prev => ({
          ...prev,
          location: {
            ...prev.location,
            address,
            city,
            state,
            zip,
            coordinates: { lat, lng }
          }
        }));
      }
    } catch (error) {
      console.error('Error reverse geocoding:', error);
      toast.error('Error getting address details');
    }
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser. Please use a modern browser or manually select a location on the map.');
      return;
    }

    setLocationLoading(true);

    // First try with high accuracy
    const tryGetLocation = (options: PositionOptions) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { longitude, latitude } = position.coords;
          if (map.current) {
            map.current.flyTo({
              center: [longitude, latitude],
              zoom: 14,
              duration: 1000
            });
          }
          updateMarker(longitude, latitude);
          setLocationLoading(false);
        },
        (error) => {
          console.error('Geolocation error:', error);
          
          // If high accuracy failed, try with low accuracy
          if (options.enableHighAccuracy) {
            console.log('Retrying with low accuracy...');
            tryGetLocation({
              enableHighAccuracy: false,
              timeout: 10000,
              maximumAge: 0
            });
            return;
          }

          let errorMessage = 'Error getting your location. ';
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage += 'Please allow location access in your browser settings or manually select a location on the map.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage += 'Location information is unavailable. Please try again or manually select a location on the map.';
              break;
            case error.TIMEOUT:
              errorMessage += 'The request to get your location timed out. Please try again or manually select a location on the map.';
              break;
            default:
              errorMessage += 'Please try again or manually select a location on the map.';
          }
          
          toast.error(errorMessage, {
            duration: 5000,
            style: {
              maxWidth: '500px',
            },
          });

          // If all attempts fail, center on a default location (Chattanooga)
          if (map.current) {
            map.current.flyTo({
              center: [-85.3088, 35.0456],
              zoom: 12,
              duration: 1000
            });
          }
          setLocationLoading(false);
        },
        options
      );
    };

    // Start with high accuracy
    tryGetLocation({
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    });
  };

  // Add a function to handle IP-based geolocation as a last resort
  const getLocationFromIP = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      if (data.latitude && data.longitude) {
        if (map.current) {
          map.current.flyTo({
            center: [data.longitude, data.latitude],
            zoom: 12,
            duration: 1000
          });
        }
        updateMarker(data.longitude, data.latitude);
        return true;
      }
    } catch (error) {
      console.error('IP geolocation error:', error);
    }
    return false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Validate required fields
    if (!formData.title || !formData.description || !formData.type || !formData.dateRange.start || !formData.dateRange.end || !formData.location.address || !formData.location.city || !formData.location.state || !formData.location.zip) {
      toast.error('Please fill in all required fields.');
      setLoading(false);
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('You must be logged in to create a listing');

      // Remove dateRange and timeRange from formData before insert
      const { dateRange, timeRange, ...rest } = formData;
      const { error } = await supabase.from('listings').insert([
        {
          ...rest,
          status: 'pending',
          created_by: user.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          date_range_start: dateRange.start,
          date_range_end: dateRange.end,
        },
      ]);

      if (error) throw error;

      toast.success('Listing created successfully!');
      setSubmitted(true);
    } catch (error: any) {
      let message = error?.message || 'An error occurred while creating the listing.';
      if (error?.code === '23505') {
        message = 'A listing with this information already exists.';
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setErrors((prev) => ({ ...prev, [name]: '' }));
    if (name.includes('.')) {
      const [parent, child, subChild] = name.split('.');
      if (subChild) {
        setFormData((prev) => ({
          ...prev,
          [parent]: {
            ...(prev[parent as keyof FormData] as Record<string, any>),
            [child]: {
              ...(prev[parent as keyof FormData] as Record<string, any>)[child],
              [subChild]: value,
            },
          },
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [parent]: {
            ...(prev[parent as keyof FormData] as Record<string, string>),
            [child]: value,
          },
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const validateStep = () => {
    const newErrors: { [key: string]: string } = {};
    if (currentStep === 'details') {
      if (!formData.title.trim()) newErrors.title = 'Title is required.';
      if (formData.title.length > 80) newErrors.title = 'Title must be 80 characters or less.';
      if (!formData.description.trim()) newErrors.description = 'Description is required.';
      if (formData.description.length > 500) newErrors.description = 'Description must be 500 characters or less.';
      if (!formData.dateRange.start) newErrors.dateRangeStart = 'Start date is required.';
      if (!formData.dateRange.end) newErrors.dateRangeEnd = 'End date is required.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      const steps: Step[] = ['type', 'details', 'location', 'preview', 'review'];
      const currentIndex = steps.indexOf(currentStep);
      setCurrentStep(steps[currentIndex + 1]);
    }
  };

  const handleBack = () => {
    const steps: Step[] = ['type', 'details', 'location', 'preview', 'review'];
    const currentIndex = steps.indexOf(currentStep);
    setCurrentStep(steps[currentIndex - 1]);
  };

  const renderStepIndicator = () => {
    const steps: { id: Step; label: string }[] = [
      { id: 'type', label: 'Type' },
      { id: 'details', label: 'Details' },
      { id: 'location', label: 'Location' },
      { id: 'preview', label: 'Preview' },
      { id: 'review', label: 'Review' },
    ];

    return (
      <div className="flex items-center justify-between mb-8">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-center">
            <div
              className={`flex items-center justify-center w-8 h-8 rounded-full ${
                currentStep === step.id
                  ? 'bg-orange-600 text-white'
                  : index < steps.findIndex((s) => s.id === currentStep)
                  ? 'bg-orange-200 text-orange-800'
                  : 'bg-gray-200 text-gray-600'
              }`}
            >
              {index + 1}
            </div>
            <span
              className={`ml-2 text-sm font-medium ${
                currentStep === step.id ? 'text-orange-600' : 'text-gray-500'
              }`}
            >
              {step.label}
            </span>
            {index < steps.length - 1 && (
              <div
                className={`w-16 h-0.5 mx-4 ${
                  index < steps.findIndex((s) => s.id === currentStep)
                    ? 'bg-orange-200'
                    : 'bg-gray-200'
                }`}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderTypeStep = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">What kind of listing are you creating?</h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          { value: 'garage-sale', label: 'Garage Sale', icon: '🏠' },
          { value: 'estate-sale', label: 'Estate Sale', icon: '🏛️' },
          { value: 'curb-alert', label: 'Curb Alert', icon: '🚪' },
        ].map((type) => (
          <button
            key={type.value}
            type="button"
            onClick={() => {
              setFormData((prev) => ({ ...prev, type: type.value as ListingType }));
              setCurrentStep('details');
            }}
            className={`flex flex-col items-center p-6 border-2 rounded-lg transition-colors ${
              formData.type === type.value
                ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-orange-300 dark:hover:border-orange-700'
            }`}
          >
            <span className="text-4xl mb-2">{type.icon}</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">{type.label}</span>
          </button>
        ))}
      </div>
    </div>
  );

  const renderDetailsStep = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Listing Details</h3>
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="title"
          name="title"
          required
          maxLength={80}
          value={formData.title}
          onChange={handleChange}
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-orange-500 focus:ring-orange-500 dark:bg-gray-700 dark:text-white sm:text-sm"
        />
        {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
      </div>
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          id="description"
          name="description"
          required
          maxLength={500}
          rows={4}
          value={formData.description}
          onChange={handleChange}
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-orange-500 focus:ring-orange-500 dark:bg-gray-700 dark:text-white sm:text-sm"
        />
        {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
      </div>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Date Range <span className="text-red-500">*</span>
          </label>
          <div className="mt-1 grid grid-cols-2 gap-4">
            <div>
              <input
                type="date"
                id="dateRange.start"
                name="dateRange.start"
                required
                value={formData.dateRange.start}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]}
                className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-orange-500 focus:ring-orange-500 dark:bg-gray-700 dark:text-white sm:text-sm"
              />
              {errors.dateRangeStart && <p className="text-red-500 text-xs mt-1">{errors.dateRangeStart}</p>}
            </div>
            <div>
              <input
                type="date"
                id="dateRange.end"
                name="dateRange.end"
                required
                value={formData.dateRange.end}
                onChange={handleChange}
                min={formData.dateRange.start || new Date().toISOString().split('T')[0]}
                className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-orange-500 focus:ring-orange-500 dark:bg-gray-700 dark:text-white sm:text-sm"
              />
              {errors.dateRangeEnd && <p className="text-red-500 text-xs mt-1">{errors.dateRangeEnd}</p>}
            </div>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Time (Optional)
          </label>
          <div className="mt-1 grid grid-cols-2 gap-4">
            <div>
              <input
                type="time"
                id="timeRange.start"
                name="timeRange.start"
                value={formData.timeRange.start}
                onChange={handleChange}
                step={3600}
                className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-orange-500 focus:ring-orange-500 dark:bg-gray-700 dark:text-white sm:text-sm"
              />
            </div>
            <div>
              <input
                type="time"
                id="timeRange.end"
                name="timeRange.end"
                value={formData.timeRange.end}
                onChange={handleChange}
                step={3600}
                className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-orange-500 focus:ring-orange-500 dark:bg-gray-700 dark:text-white sm:text-sm"
              />
            </div>
          </div>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Images
        </label>
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={async (e) => {
            const files = Array.from(e.target.files || []);
            if (files.length === 0) return;
            // Upload images to Supabase Storage or another service
            // For now, just use local URLs for preview
            const urls = await Promise.all(files.map(file => new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.readAsDataURL(file);
            })));
            setFormData(prev => ({ ...prev, images: urls as string[] }));
          }}
          className="mt-1 block w-full text-sm text-gray-900 dark:text-gray-200 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
        />
        {formData.images && formData.images.length > 0 && (
          <div className="flex mt-2 space-x-2">
            {formData.images.map((img, idx) => (
              <img key={idx} src={img} alt={`Preview ${idx + 1}`} className="w-16 h-16 object-cover rounded border" />
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderLocationStep = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Location Details</h3>
      
      <div className="h-[400px] rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
        <div ref={mapContainer} className="w-full h-full" />
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={handleUseCurrentLocation}
            disabled={locationLoading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {locationLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Getting location...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Use My Location
              </>
            )}
          </button>
          <button
            type="button"
            onClick={getLocationFromIP}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
            Use Approximate Location
          </button>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Click on the map or drag the pin to set location
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label htmlFor="address" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Address
          </label>
          <input
            type="text"
            id="address"
            name="location.address"
            required
            value={formData.location.address}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-orange-500 focus:ring-orange-500 dark:bg-gray-700 dark:text-white sm:text-sm"
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              City
            </label>
            <input
              type="text"
              id="city"
              name="location.city"
              required
              value={formData.location.city}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-orange-500 focus:ring-orange-500 dark:bg-gray-700 dark:text-white sm:text-sm"
            />
          </div>
          <div>
            <label htmlFor="state" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              State
            </label>
            <input
              type="text"
              id="state"
              name="location.state"
              required
              value={formData.location.state}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-orange-500 focus:ring-orange-500 dark:bg-gray-700 dark:text-white sm:text-sm"
            />
          </div>
          <div>
            <label htmlFor="zip" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              ZIP
            </label>
            <input
              type="text"
              id="zip"
              name="location.zip"
              required
              value={formData.location.zip}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-orange-500 focus:ring-orange-500 dark:bg-gray-700 dark:text-white sm:text-sm"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderPreviewStep = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Preview Your Listing</h3>
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6 space-y-4">
        <div>
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Title</h4>
          <p className="mt-1 text-sm text-gray-900 dark:text-white">{formData.title}</p>
        </div>
        <div>
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Description</h4>
          <p className="mt-1 text-sm text-gray-900 dark:text-white">{formData.description}</p>
        </div>
        <div>
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Date</h4>
          <p className="mt-1 text-sm text-gray-900 dark:text-white">{formData.dateRange.start} to {formData.dateRange.end}</p>
        </div>
        <div>
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Time</h4>
          <p className="mt-1 text-sm text-gray-900 dark:text-white">{formData.timeRange.start} to {formData.timeRange.end}</p>
        </div>
        <div>
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Location</h4>
          <p className="mt-1 text-sm text-gray-900 dark:text-white">
            {formData.location.address}<br />
            {formData.location.city}, {formData.location.state} {formData.location.zip}
          </p>
        </div>
        <div>
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Images</h4>
          <div className="flex flex-wrap gap-2 mt-2">
            {formData.images && formData.images.length > 0 ? (
              formData.images.map((img, idx) => (
                <img key={idx} src={img} alt={`Preview ${idx + 1}`} className="w-24 h-24 object-cover rounded border" />
              ))
            ) : (
              <span className="text-gray-400">No images uploaded.</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderReviewStep = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Review Your Listing</h3>
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6 space-y-4">
        <div>
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Type</h4>
          <p className="mt-1 text-sm text-gray-900 dark:text-white capitalize">{formData.type}</p>
        </div>
        <div>
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Title</h4>
          <p className="mt-1 text-sm text-gray-900 dark:text-white">{formData.title}</p>
        </div>
        <div>
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Description</h4>
          <p className="mt-1 text-sm text-gray-900 dark:text-white">{formData.description}</p>
        </div>
        <div>
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Date Range</h4>
          <p className="mt-1 text-sm text-gray-900 dark:text-white">
            {formData.dateRange.start} to {formData.dateRange.end}
            {formData.timeRange.start && (
              <span>
                <br />
                Time: {formData.timeRange.start}
                {formData.timeRange.end && ` to ${formData.timeRange.end}`}
              </span>
            )}
          </p>
        </div>
        <div>
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Location</h4>
          <p className="mt-1 text-sm text-gray-900 dark:text-white">
            {formData.location.address}
            <br />
            {formData.location.city}, {formData.location.state} {formData.location.zip}
          </p>
        </div>

        {/* Review conditional fields */}
        {formData.type === 'estate-sale' && (
          <>
            {formData.estateCompany && (
              <div>
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Estate Sale Company</h4>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{formData.estateCompany}</p>
              </div>
            )}
            {formData.previewDate && (
              <div>
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Preview</h4>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">
                  {formData.previewDate} at {formData.previewTime}
                </p>
              </div>
            )}
          </>
        )}

        {formData.type === 'curb-alert' && (
          <>
            {formData.itemCondition && (
              <div>
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Item Condition</h4>
                <p className="mt-1 text-sm text-gray-900 dark:text-white capitalize">{formData.itemCondition}</p>
              </div>
            )}
            {formData.availableUntil && (
              <div>
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Available Until</h4>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">
                  {new Date(formData.availableUntil).toLocaleString()}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 'type':
        return renderTypeStep();
      case 'details':
        return renderDetailsStep();
      case 'location':
        return renderLocationStep();
      case 'preview':
        return renderPreviewStep();
      case 'review':
        return renderReviewStep();
      default:
        return null;
    }
  };

  const renderNavigation = () => (
    <div className="mt-8 flex justify-between">
      {currentStep !== 'type' && (
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
        >
          Back
        </button>
      )}
      {currentStep !== 'review' ? (
        <button
          type="button"
          onClick={handleNext}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
        >
          Next
        </button>
      ) : (
        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Creating...' : 'Create Listing'}
        </button>
      )}
    </div>
  );

  if (submitted) return <ListingSubmitted />;

  return (
    <div className="max-w-3xl mx-auto bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Create New Listing</h2>
      {renderStepIndicator()}
      <form onSubmit={handleSubmit} className="space-y-6">
        {renderStepContent()}
        {renderNavigation()}
      </form>
    </div>
  );
} 